/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { Fragment, useState, useContext } from 'react';
import { Dialog, Menu, Transition } from '@headlessui/react';
import { Context as AuthContext } from 'contexts/auth';
import { ReactComponent as Logo } from 'assets/images/logo/logo-full-white.svg';
import {
  HomeIcon,
  ClipboardListIcon,
  CreditCardIcon,
  MenuAlt2Icon,
  ShoppingCartIcon,
  XIcon,
  StarIcon,
  BookOpenIcon,
  CashIcon,
  CalculatorIcon,
  ExternalLinkIcon,
  ArchiveIcon,
} from '@heroicons/react/outline';
import { useLocation, Outlet, Link, useNavigate } from 'react-router-dom';
import { joinClassNames } from 'utils/cssHelper';
import ProfileLogo from 'assets/images/logo/logo192.png';
import { pushHandlerSingleton } from 'services/oneSignal';
import { SidebarLinkGroup } from './sidebarLinkGroup';

const navigation = [
  {
    name: 'Dashboard',
    href: '/',
    icon: HomeIcon,
    current: false,
  },
  {
    name: 'Orders',
    href: '/orders',
    icon: ClipboardListIcon,
    current: false,
  },
  {
    name: 'Menu',
    href: '/menu',
    icon: BookOpenIcon,
    current: false,
  },
  {
    name: 'Products',
    href: '/products',
    icon: ShoppingCartIcon,
    current: false,
  },
  {
    name: 'Discounts',
    href: '/discounts',
    icon: CreditCardIcon,
    current: false,
  },
  {
    name: 'Reviews',
    href: '/reviews',
    icon: StarIcon,
    current: false,
  },
  {
    name: 'Payments',
    href: '/payments',
    icon: CashIcon,
    current: false,
  },
  {
    name: 'Assign Tips',
    href: '/assign-tips',
    icon: CalculatorIcon,
    current: false,
  },
  {
    name: 'Additional',
    icon: ArchiveIcon,
    current: false,
    expandable: true,
    href: '/additional',
    children: [
      {
        name: 'Events',
        current: false,
        href: '/additional/events',
      },
      {
        name: 'Promotions',
        current: false,
        href: '/additional/promotions',
      },
    ],
  },
];

const secondaryNavigation = [
  { name: 'Restaurants', href: '/settings/restaurants' },
  { name: 'Tables', href: '/settings/tables' },
  // { name: 'Payment accounts', href: '/settings/payment-accounts' },
  { name: 'Employees', href: '/settings/employees' },
  { name: 'Product categories', href: '/settings/product-categories' },
  { name: 'Variation templates', href: '/settings/variation-templates' },
  { name: 'Variation joins', href: '/settings/variation-joins' },
  // { name: 'Drivers', href: '/settings/drivers' },
  // { name: 'Delivery', href: '/settings/delivery' },
];

const userNavigation = [
  { name: 'Your Profile', href: '/profile' },
  { name: 'Account Settings', href: '/account' },
  { name: 'Sign out', href: '/logout' },
];

export const MainLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const { state: authState } = useContext(AuthContext);

  const onboardingCompleted = authState.company?.onboarding_completed || false;
  const billingPaymentLink = authState.company?.payment_link || null;

  const isPathCurrent = (path) => {
    if (location.pathname === '/' && path === '/') {
      return true;
    }

    if (location.pathname === '/settings' || path === '/settings') {
      return path === location.pathname;
    }

    if (path === '/additional') {
      return false;
    }

    return location.pathname.startsWith(path) && path !== '/';
  };

  return (
    <div>
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 flex z-40 md:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-gray-800">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button
                    type="button"
                    className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white bg-gray-800"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XIcon className="h-6 w-6 text-white " aria-hidden="true" />
                  </button>
                </div>
              </Transition.Child>
              <div className="flex-shrink-0 flex items-center px-4">
                <Logo className="mx-auto w-28" />
              </div>
              <div className="mt-5 flex-1 h-0 overflow-y-auto">
                <nav className="px-2 space-y-1">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={joinClassNames(
                        isPathCurrent(item.href)
                          ? 'bg-gray-900 text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                        'group flex items-center px-2 py-2 text-base font-medium rounded-md',
                      )}
                      onClick={() => { setSidebarOpen(false); }}
                    >
                      <item.icon
                        className={joinClassNames(
                          isPathCurrent(item.href)
                            ? 'text-gray-300'
                            : 'text-gray-400 group-hover:text-gray-300',
                          'mr-4 flex-shrink-0 h-6 w-6',
                        )}
                        aria-hidden="true"
                      />
                      {item.name}
                    </Link>
                  ))}

                  <div className="pt-10">
                    <p
                      className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider"
                      id="submenu-headline"
                    >
                      Settings
                    </p>
                    <div className="mt-3 space-y-2" aria-labelledby="submenu-headline">
                      {secondaryNavigation.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={joinClassNames(
                            'group flex items-center px-3 py-2 text-sm font-medium rounded-md hover:bg-gray-50 hover:text-white',
                            isPathCurrent(item.href)
                              ? 'bg-gray-900 hover:bg-gray-900 text-white hover:text-white'
                              : 'hover:bg-gray-700 text-gray-600',
                          )}
                          onClick={() => { setSidebarOpen(false); }}
                        >
                          <span className="truncate">{item.name}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </nav>
              </div>
            </div>
          </Transition.Child>
          <div className="flex-shrink-0 w-14" aria-hidden="true">
            {/* Dummy element to force sidebar to shrink to fit close icon */}
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0">
        {/* Sidebar component, swap this element with another sidebar if you like */}
        <div className="flex-1 flex flex-col min-h-0 bg-gray-800">
          <div className="flex items-center h-16 flex-shrink-0 px-4 bg-gray-900">
            <Logo className="mx-auto w-28" />
          </div>
          <div className="mt-10 flex-1 flex flex-col overflow-y-auto">
            <div className="space-y-1">
              <nav className="flex-1 px-2">
                <div className="pb-8 space-y-1">
                  {navigation.map((item) => {
                    if (item.expandable) {
                      return (
                        <SidebarLinkGroup
                          key={item.name}
                          activeCondition={isPathCurrent(item.href)}
                        >
                          {(handleClick, open) => {
                            return (
                              <>
                                <button
                                  type="button"
                                  className={joinClassNames(
                                    isPathCurrent(item.href)
                                      ? 'bg-gray-900 text-white'
                                      : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                                    'group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full relative',
                                  )}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleClick();
                                  }}
                                >
                                  <item.icon
                                    className={joinClassNames(
                                      isPathCurrent(item.href)
                                        ? 'text-gray-300'
                                        : 'text-gray-400 group-hover:text-gray-300',
                                      'mr-3 flex-shrink-0 h-6 w-6',
                                    )}
                                    aria-hidden="true"
                                  />
                                  {item.name}
                                  <svg
                                    className={`absolute right-4 top-1/2 -translate-y-1/2 fill-current ${
                                      open && 'rotate-180'
                                    }`}
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M4.41107 6.9107C4.73651 6.58527 5.26414 6.58527 5.58958 6.9107L10.0003 11.3214L14.4111 6.91071C14.7365 6.58527 15.2641 6.58527 15.5896 6.91071C15.915 7.23614 15.915 7.76378 15.5896 8.08922L10.5896 13.0892C10.2641 13.4147 9.73651 13.4147 9.41107 13.0892L4.41107 8.08922C4.08563 7.76378 4.08563 7.23614 4.41107 6.9107Z"
                                      fill=""
                                    />
                                  </svg>
                                </button>
                                {/* <!-- Dropdown Menu Start --> */}
                                <div
                                  className={`translate transform overflow-hidden ${
                                    !open && 'hidden'
                                  }`}
                                >
                                  <ul className="mt-1 mb-1 flex flex-col pl-8">
                                    {item.children.map((childItem) => {
                                      return (
                                        <li>
                                          <Link
                                            key={childItem.name}
                                            to={childItem.href}
                                            className={joinClassNames(
                                              isPathCurrent(childItem.href)
                                                ? 'bg-gray-900 text-white'
                                                : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                                              'group flex items-center px-2 py-2 text-sm font-medium rounded-md',
                                            )}
                                          >
                                            {childItem.name}
                                          </Link>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                                {/* <!-- Dropdown Menu End --> */}
                              </>
                            );
                          }}
                        </SidebarLinkGroup>
                      );
                    }

                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={joinClassNames(
                          isPathCurrent(item.href)
                            ? 'bg-gray-900 text-white'
                            : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                          'group flex items-center px-2 py-2 text-sm font-medium rounded-md',
                        )}
                      >
                        <item.icon
                          className={joinClassNames(
                            isPathCurrent(item.href)
                              ? 'text-gray-300'
                              : 'text-gray-400 group-hover:text-gray-300',
                            'mr-3 flex-shrink-0 h-6 w-6',
                          )}
                          aria-hidden="true"
                        />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>

                <div className="pt-10">
                  <p
                    className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider"
                    id="submenu-headline"
                  >
                    Settings
                  </p>
                  <div className="mt-3 space-y-2" aria-labelledby="submenu-headline">
                    {secondaryNavigation.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={joinClassNames(
                          'group flex items-center px-3 py-2 text-sm font-medium rounded-md hover:bg-gray-50 hover:text-white',
                          isPathCurrent(item.href)
                            ? 'bg-gray-900 hover:bg-gray-900 text-white hover:text-white'
                            : 'hover:bg-gray-700 text-gray-600',
                        )}
                      >
                        <span className="truncate">{item.name}</span>
                      </Link>
                    ))}
                    {!!billingPaymentLink && (
                      <a
                        href={billingPaymentLink}
                        target="_blank"
                        className={joinClassNames(
                          'group flex items-center px-3 py-2 text-sm font-medium rounded-md hover:bg-gray-50 hover:text-white',
                          'hover:bg-gray-700 text-gray-600',
                        )}
                        rel="noreferrer"
                      >
                        <span>Billing</span>
                        <ExternalLinkIcon className="h-4 w-4 ml-2" aria-hidden="true" />
                      </a>
                    )}
                  </div>
                </div>
              </nav>
            </div>
          </div>
        </div>
      </div>
      <div className="md:pl-64 flex flex-col">
        <div className="sticky top-0 z-20 flex-shrink-0 flex h-16 bg-white shadow">
          <button
            type="button"
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <MenuAlt2Icon className="h-6 w-6" aria-hidden="true" />
          </button>
          <div className="flex-1 px-4 flex justify-end">

            <div className="ml-4 flex items-center md:ml-6">

              {/* Profile dropdown */}
              <Menu as="div" className="ml-3 relative">
                <div>
                  <Menu.Button className="max-w-xs bg-white flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                    <span className="sr-only">Open user menu</span>
                    <img
                      className="h-8 w-8 rounded-full"
                      src={ProfileLogo}
                      alt=""
                    />
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                    {userNavigation.map((item) => (
                      <Menu.Item key={item.name}>
                        {({ active }) => (
                          <Link
                            to={item.href}
                            className={joinClassNames(
                              active ? 'bg-gray-100' : '',
                              'block px-4 py-2 text-sm text-gray-700',
                            )}
                          >{item.name}
                          </Link>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>
        {!onboardingCompleted && (
        <div className="sticky z-10 top-16 left-0 w-full">
          <div className="w-full flex items-center justify-between p-4 text-yellow-800 border-t-4 border-yellow-300 bg-yellow-50" role="alert">
            <div className="flex items-center">
              <svg className="flex-shrink-0 w-4 h-4" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z" />
              </svg>
              <div className="ml-4 mr-4 text-sm font-medium">
                Please complete onboarding process
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                navigate('/onboarding');
              }}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
            >
              Go to form
            </button>
          </div>
        </div>
        )}

        <div className="sticky z-10 top-16 left-0 w-full hidden" id="order-alert-banner">
          <div className="w-full flex items-center justify-between p-4 text-yellow-800 border-t-4 border-yellow-300 bg-yellow-50" role="alert">
            <div className="flex items-center">
              <svg className="flex-shrink-0 w-4 h-4" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z" />
              </svg>
              <div className="ml-4 mr-4 text-sm font-medium" id="order-alert-banner-text">
                -
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                pushHandlerSingleton.handler.haveInteracted();
                pushHandlerSingleton.handler.clearNotificationBanner();
              }}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
            >
              Confirm
            </button>
          </div>
        </div>
        <main className="flex-1">
          <div className="pt-6 pb-10">
            <div className="mx-5 sm:mx-5 md:mx-15 lg:mx-20 xl:mx-30 2xl:mx-40">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};
